import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { PageHero, Reveal } from "@/components/Motion";
import ProductCard from "@/components/ProductCard";
import BrochureButton from "@/components/BrochureButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";

const CATS = [
  { value: "", label: "All" },
  { value: "rice", label: "Rice" },
  { value: "spices", label: "Spices" },
  { value: "tea", label: "Tea" },
];

export default function Products({ fixedCategory = "" }) {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(fixedCategory);
  const [form, setForm] = useState("");
  const [sort, setSort] = useState("name_asc");
  const [quickView, setQuickView] = useState(null);

  useEffect(() => setCategory(fixedCategory), [fixedCategory]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (form) params.set("form", form);
    params.set("sort", sort);
    api.get(`/products?${params.toString()}`)
      .then((r) => setProducts(r.data.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [search, category, form, sort]);

  const allForms = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.forms || []).forEach((f) => s.add(f)));
    return [...s];
  }, [products]);

  const catMeta = settings?.categories?.[fixedCategory];
  const seoPage = settings?.seo?.pages?.products;

  return (
    <div data-testid="products-page">
      <SEO
        title={catMeta ? `${catMeta.title} — Indian ${catMeta.title} Exporter | AnVibes Exports` : seoPage?.title}
        description={catMeta ? (fixedCategory === "tea" ? "Sourcing Indian Green Tea, Black Tea, and CTC Tea for Global B2B Buyers." : catMeta.description) : seoPage?.description}
        path={fixedCategory ? `/${fixedCategory}` : "/products"}
      />
      <PageHero
        eyebrow={fixedCategory ? `Category — ${catMeta?.title || fixedCategory}` : "Product Catalog"}
        title={catMeta ? `Indian ${catMeta.title}` : "Spices, Rice & Teas"}
        description={catMeta ? (fixedCategory === "tea" ? "Sourcing Indian Green Tea, Black Tea, and CTC Tea for Global B2B Buyers." : catMeta.description) : "Indian spices, rice and teas sourced for international B2B buyers, wholesalers, distributors and food businesses. Every product page includes specifications, packaging and MOQ details."}
        image={catMeta?.image}
      />

      <section className="bg-parchment py-14 lg:py-20">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          {settings?.brochure?.enabled && settings?.brochure?.url && (
            <Reveal>
              <div className="mb-6 flex flex-col gap-4 border border-gold/40 bg-gold/10 px-6 py-5 sm:flex-row sm:items-center sm:justify-between" data-testid="brochure-banner">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-dark">For buyers & distributors</p>
                  <p className="mt-1 font-serif text-xl text-forest">Take the full range with you — download our {settings.brochure.title || "Product Catalogue"}.</p>
                </div>
                <BrochureButton />
              </div>
            </Reveal>
          )}
          <Reveal>
            <div className="flex flex-col gap-4 border border-forest/10 bg-white p-5 lg:flex-row lg:items-center" data-testid="catalog-toolbar">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-forest/40" />
                <input
                  data-testid="product-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products — cloves, basmati, green tea..."
                  className="w-full border border-forest/15 bg-parchment/50 py-3 pl-10 pr-10 text-sm text-forest placeholder:text-forest/35 focus:border-gold focus:outline-none"
                />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/40 hover:text-forest" aria-label="Clear search">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {!fixedCategory && (
                <div className="flex gap-1.5" data-testid="category-filters">
                  {CATS.map((c) => (
                    <button
                      key={c.value}
                      data-testid={`filter-category-${c.value || "all"}`}
                      onClick={() => setCategory(c.value)}
                      className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                        category === c.value ? "bg-forest text-parchment" : "border border-forest/15 text-forest/70 hover:border-gold hover:text-gold-dark"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
              <select data-testid="filter-form" value={form} onChange={(e) => setForm(e.target.value)} className="border border-forest/15 bg-white px-4 py-3 text-sm text-forest focus:border-gold focus:outline-none">
                <option value="">All forms</option>
                {allForms.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-forest/15 bg-white px-4 py-3 text-sm text-forest focus:border-gold focus:outline-none">
                <option value="name_asc">Name A → Z</option>
                <option value="name_desc">Name Z → A</option>
                <option value="newest">Newest first</option>
              </select>
            </div>
          </Reveal>

          <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-forest/50" data-testid="catalog-count">
            {loading ? "Loading..." : `${products.length} product${products.length === 1 ? "" : "s"}`}
          </p>

          {loading ? (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white border border-forest/10">
                  <div className="aspect-[4/3] bg-forest/5" />
                  <div className="p-5 space-y-3"><div className="h-5 w-2/3 bg-forest/5" /><div className="h-3 w-full bg-forest/5" /></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="mt-10 border border-forest/10 bg-white p-14 text-center" data-testid="catalog-empty">
              <p className="font-serif text-2xl text-forest">No products match your filters.</p>
              <p className="mt-2 text-sm text-[#4A554D]">Try a different search or clear the filters.</p>
            </div>
          ) : (
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="product-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} onQuickView={setQuickView} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!quickView} onOpenChange={() => setQuickView(null)}>
        <DialogContent className="max-w-2xl bg-parchment" data-testid="quick-view-modal">
          {quickView && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-forest">{quickView.name}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="aspect-[4/3] overflow-hidden clip-frame-sm bg-forest/5">
                  {quickView.images?.[0] && <img src={quickView.images[0]} alt={quickView.name} className="h-full w-full object-cover" />}
                </div>
                <div>
                  <p className="text-sm text-[#4A554D] leading-relaxed">{quickView.short_description}</p>
                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-4 border-b border-forest/10 pb-2"><dt className="text-forest/50">Origin</dt><dd className="text-forest font-medium">{quickView.origin}</dd></div>
                    <div className="flex justify-between gap-4 border-b border-forest/10 pb-2"><dt className="text-forest/50">MOQ</dt><dd className="text-forest font-medium">{quickView.moq}</dd></div>
                    <div className="flex justify-between gap-4 border-b border-forest/10 pb-2"><dt className="text-forest/50">Forms</dt><dd className="text-forest font-medium text-right">{(quickView.forms || []).join(", ") || "—"}</dd></div>
                  </dl>
                  <div className="mt-5 flex gap-2.5">
                    <Link to={`/products/${quickView.slug}`} data-testid="quick-view-details" className="border border-forest/20 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold hover:text-gold-dark transition-colors">
                      Full Details
                    </Link>
                    <Link to={`/quote?product=${encodeURIComponent(quickView.name)}&category=${quickView.category}`} data-testid="quick-view-quote" className="bg-forest px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-parchment hover:bg-forest-leaf transition-colors">
                      Request Quote
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
